import {
  arbitrum,
  aurora,
  avalanche,
  base,
  bsc,
  type Chain,
  fantom,
  gnosis,
  mainnet,
  optimism,
  polygon,
} from 'viem/chains';

import { GENERAL_URLS } from '@shared/constants/urls';

import type { CoreTypes } from '@walletconnect/types';

type SupportedNetwork = {
  chain: Chain;
  /**
   * Optional overrides used when adding a new network to a wallet (e.g., MetaMask).
   * While these values may be used by different wallets, MetaMask is known to apply
   * especially strict validation when adding networks. Any mismatch between the
   * provided parameters and its internal list can lead to warnings or even prevent
   * the network from being added.
   *
   * This field allows customizing the network name or RPC URL to match expected values.
   * The `rpcUrl` here is used only during the "add network" flow.
   * Actual RPC requests will use URLs from the `rpcUrls` array.
   * The `rpcUrl` should match one of the URLs listed at:
   * https://chainid.network/chains.json
   */
  overrides?: {
    name?: string;
    rpcUrl?: string;
  };
  /**
   * RPC endpoints used for blockchain requests (not for wallet network addition).
   */
  rpcUrls: string[];
};

export const SUPPORTED_NETWORKS: SupportedNetwork[] = [
  {
    chain: mainnet,
    rpcUrls: [
      'https://ethereum-rpc.publicnode.com',
      'https://1rpc.io/scroll',
      'https://eth.drpc.org',
    ],
  },
  {
    chain: bsc,
    overrides: {
      name: 'BNB Smart Chain Mainnet',
      rpcUrl: 'https://bsc-dataseed1.bnbchain.org',
    },
    rpcUrls: ['https://bsc-rpc.publicnode.com', 'https://1rpc.io/bnb', 'https://bsc.drpc.org'],
  },
  {
    chain: polygon,
    overrides: {
      name: 'Polygon Mainnet',
    },
    rpcUrls: [
      'https://polygon-bor-rpc.publicnode.com',
      'https://1rpc.io/matic',
      'https://polygon.drpc.org',
    ],
  },
  {
    chain: optimism,
    rpcUrls: [
      'https://optimism-rpc.publicnode.com',
      'https://1rpc.io/op',
      'https://optimism.drpc.org',
    ],
  },
  {
    chain: arbitrum,
    rpcUrls: [
      'https://arbitrum-one-rpc.publicnode.com',
      'https://1rpc.io/arb',
      'https://arbitrum.drpc.org',
    ],
  },
  {
    chain: gnosis,
    rpcUrls: [
      'https://gnosis-rpc.publicnode.com',
      'https://1rpc.io/gnosis',
      'https://gnosis.drpc.org',
    ],
  },
  {
    chain: avalanche,
    overrides: {
      name: 'Avalanche C-Chain',
    },
    rpcUrls: ['https://avalanche-c-chain-rpc.publicnode.com', 'https://avalanche.drpc.org'],
  },
  {
    chain: fantom,
    overrides: {
      name: 'Fantom Opera',
      rpcUrl: 'https://fantom-rpc.publicnode.com',
    },
    rpcUrls: ['https://fantom-rpc.publicnode.com', 'https://fantom.drpc.org'],
  },
  {
    chain: aurora,
    overrides: {
      name: 'Aurora Mainnet',
    },
    rpcUrls: [
      'https://aurora-rpc.publicnode.com',
      'https://1rpc.io/aurora',
      'https://aurora.drpc.org',
    ],
  },
  {
    chain: base,
    rpcUrls: ['https://base-rpc.publicnode.com', 'https://1rpc.io/base', 'https://base.drpc.org'],
  },
];

const metadataConfig = {
  'store-web': {
    name: 'Magic Store',
    description: 'Crypto App Store by Magic Square',
    url: 'https://magicsquare.io',
    icons: ['https://magicsquare.io/wp-content/uploads/2022/06/MSQ-logo-short.svg'],
  },
  staking: {
    name: 'Magic Staking',
    description: 'Crypto App Store by Magic Square',
    url: GENERAL_URLS.staking,
    icons: ['https://magicsquare.io/wp-content/uploads/2022/06/MSQ-logo-short.svg'],
  },
  launchpad: {
    name: 'Magic Launchpad',
    description: 'Crypto App Store by Magic Square',
    url: 'https://launchpad.magicsquare.io',
    icons: ['https://magicsquare.io/wp-content/uploads/2022/06/MSQ-logo-short.svg'],
  },
  empty: {
    name: 'Magic Square',
    description: 'Magic Square',
    url: 'https://magicsquare.io',
    icons: ['https://magicsquare.io/wp-content/uploads/2022/06/MSQ-logo-short.svg'],
  },
} as const satisfies Record<string, CoreTypes.Metadata>;

export const PROJECT_NAME =
  typeof process !== 'undefined'
    ? (process.env.NEXT_PUBLIC_PROJECT_NAME as keyof typeof metadataConfig)
    : undefined;

export const metadata =
  (PROJECT_NAME && PROJECT_NAME in metadataConfig && metadataConfig[PROJECT_NAME]) ||
  metadataConfig.empty;
